import React, { useMemo } from 'react';
import { MdLocationOn, MdPeople, MdBusiness } from 'react-icons/md';
import BaseChart from '../../../Components/Analytics/BaseChart';
import { useChartTheme } from '../../../Context/ThemeContext';
import {
  getTopKPIs,
  transformProviderPerformance,
  transformFacilityPatients,
  transformAppointmentStatus,
  transformFacilityPerformanceRadar,
  transformAgeDistribution,
  transformCompletionGauge,
  transformAppointmentFunnel,
  transformCityDistribution,
  transformStateDistribution,
  transformPatientsAppointmentsVisits,
  transformFacilityTreemap,
  transformAgeHeatmap,
  transformFinancialScatter,
  transformSankey,
  transformFinancialByFacility,
} from '../../../Utils/AnalyticsTransformers';
import {
  providerPerformanceOption,
  facilityPatientsOption,
  appointmentStatusOption,
  facilityPerformanceRadarOption,
  ageDistributionOption,
  completionGaugeOption,
  appointmentFunnelOption,
  stateDistributionOption,
  cityDistributionOption,
  patientsComparisonOption,
  facilityTreemapOption,
  ageHeatmapOption,
  financialScatterOption,
  sankeyOption,
  financialByFacilityOption,
} from '../../../Utils/AnalyticsOptions';

export default function AnalyticsOrganizationCharts({ analyticsData = [], loading = false, error = null }) {
  useChartTheme();
  const analytics = useMemo(() => (Array.isArray(analyticsData) ? analyticsData : []), [analyticsData]);
  const hasData = analytics.length > 0;

  const kpis = useMemo(() => getTopKPIs(analytics), [analytics]);
  const providerPerformanceData = useMemo(() => transformProviderPerformance(analytics), [analytics]);
  const facilityPatientsData = useMemo(() => transformFacilityPatients(analytics), [analytics]);
  const appointmentStatusData = useMemo(() => transformAppointmentStatus(analytics), [analytics]);
  const facilityPerformanceRadarData = useMemo(() => transformFacilityPerformanceRadar(analytics), [analytics]);
  const ageDistributionData = useMemo(() => transformAgeDistribution(analytics), [analytics]);
  const completionGaugeData = useMemo(() => transformCompletionGauge(analytics), [analytics]);
  const appointmentFunnelData = useMemo(() => transformAppointmentFunnel(analytics), [analytics]);
  const stateDistributionData = useMemo(() => transformStateDistribution(analytics), [analytics]);
  const cityDistributionData = useMemo(() => transformCityDistribution(analytics), [analytics]);
  const patientsComparisonData = useMemo(() => transformPatientsAppointmentsVisits(analytics), [analytics]);
  const facilityTreemapData = useMemo(() => transformFacilityTreemap(analytics), [analytics]);
  const heatmapData = useMemo(() => transformAgeHeatmap(analytics), [analytics]);
  const financialScatterData = useMemo(() => transformFinancialScatter(analytics), [analytics]);
  const sankeyData = useMemo(() => transformSankey(analytics), [analytics]);
  const financialByFacilityData = useMemo(() => transformFinancialByFacility(analytics), [analytics]);

  const metricCards = [
    {
      label: 'Patients',
      value: kpis.patients.toLocaleString(),
      icon: <MdPeople size={22} className="hc-text-primary" />,
    },
    {
      label: 'Appointments',
      value: kpis.appointments.toLocaleString(),
      icon: <MdBusiness size={22} className="hc-text-primary" />,
    },
    {
      label: 'Visits',
      value: kpis.visits.toLocaleString(),
      icon: <MdLocationOn size={22} className="hc-text-primary" />,
    },
    {
      label: 'Facilities',
      value: kpis.facilities.toLocaleString(),
      // description: 'Facilities included in analytics',
      icon: <MdBusiness size={22} className="hc-text-primary" />,
    },
  ];

  return (
    <div className="row g-4">
      <div className="col-12">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-2 mb-4">
          <div>
            <h3 className="fw-bold mb-1">Organization charts</h3>
            <p className="text-secondary mb-0">Visualize organization productivity and facility performance from the selected date range.</p>
          </div>
          <div className="text-muted small">{hasData ? `${analytics.length} facilities included` : 'Waiting for analytics'}</div>
        </div>
      </div>

      <div className="col-12">
        <div className="row g-3">
          {metricCards.map((card) => (
            <div key={card.label} className="col-12 col-sm-6 col-xl-3">
              <div className="hc-surface rounded-4 p-3 h-100 d-flex flex-column justify-content-between">
                <div className="d-flex align-items-start gap-3 ">
                  <div className="d-inline-flex align-items-center justify-content-center rounded-4" style={{ width: 48, height: 48, backgroundColor: 'rgba(var(--hc-icon-navy-blue-rgb), 0.12)' }}>
                    {card.icon}
                  </div>
                  <div>
                    <div className="text-secondary small text-uppercase fw-semibold">{card.label}</div>
                    <div className="fw-bold fs-4 mt-1">{card.value}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="col-12 col-xl-6">
        <BaseChart
          title="Provider performance"
          subtitle="Revenue and appointment contribution by provider"
          loading={loading}
          noData={!loading && !hasData}
          error={error}
          option={providerPerformanceOption({ data: providerPerformanceData })}
        />
      </div>

      <div className="col-12 col-xl-6">
        <BaseChart
          title="Facility patient volume"
          subtitle="Patient counts per facility"
          loading={loading}
          noData={!loading && !hasData}
          error={error}
          option={facilityPatientsOption({ data: facilityPatientsData })}
        />
      </div>

      <div className="col-12 col-xl-6">
        <BaseChart
          title="Appointment status"
          subtitle="Status distribution across the organization"
          loading={loading}
          noData={!loading && !hasData}
          error={error}
          option={appointmentStatusOption({ data: appointmentStatusData })}
        />
      </div>

      <div className="col-12 col-xl-6">
        <BaseChart
          title="Facility performance radar"
          subtitle="Compare core metrics across facilities"
          loading={loading}
          noData={!loading && !hasData}
          error={error}
          option={facilityPerformanceRadarOption({ data: facilityPerformanceRadarData })}
        />
      </div>

      <div className="col-12 col-xl-6">
        <BaseChart
          title="Age distribution"
          subtitle="Patient age groups for appointments"
          loading={loading}
          noData={!loading && !hasData}
          error={error}
          option={ageDistributionOption({ data: ageDistributionData })}
        />
      </div>

      <div className="col-12 col-xl-6">
        <BaseChart
          title="Completion rate"
          subtitle="Seen appointment percentage"
          loading={loading}
          noData={!loading && !hasData}
          error={error}
          option={completionGaugeOption({ value: completionGaugeData.value })}
        />
      </div>

      <div className="col-12 col-xl-6">
        <BaseChart
          title="Appointment funnel"
          subtitle="Journey from scheduling to seen"
          loading={loading}
          noData={!loading && !hasData}
          error={error}
          option={appointmentFunnelOption({ data: appointmentFunnelData })}
        />
      </div>

      <div className="col-12 col-xl-6">
        <BaseChart
          title="Financial summary by facility"
          subtitle="Insurance pay vs patient payment, totals"
          loading={loading}
          noData={!loading && !hasData}
          error={error}
          option={financialByFacilityOption({ data: financialByFacilityData })}
        />
      </div>

      {/* <div className="col-12 col-xl-6">
        <div style={{ overflowX: 'auto' }}>
          <BaseChart
            title="Financial bubble chart (detail)"
            subtitle="Insurance pay vs patient payment, per provider/CPT (log scale)"
            loading={loading}
            noData={!loading && !hasData}
            error={error}
            option={financialScatterOption({ data: financialScatterData })}
          />
        </div>
      </div> */}

      <div className="col-12 col-xl-6">
        <BaseChart
          title="State distribution"
          subtitle="Appointment concentration by state"
          loading={loading}
          noData={!loading && !hasData}
          error={error}
          option={stateDistributionOption({ data: stateDistributionData })}
        />
      </div>

      <div className="col-12 col-xl-6">
        <BaseChart
          title="Top patient cities"
          subtitle="Most common appointment cities"
          loading={loading}
          noData={!loading && !hasData}
          error={error}
          option={cityDistributionOption({ data: cityDistributionData })}
        />
      </div>

      <div className="col-12 col-xl-6">
        <BaseChart
          title="Patient vs appointments"
          subtitle="Compare patient count and appointment count"
          loading={loading}
          noData={!loading && !hasData}
          error={error}
          option={patientsComparisonOption({ data: patientsComparisonData, comparison: 'Appointments', name: 'Patient comparisons' })}
        />
      </div>

      <div className="col-12 col-xl-6">
        <BaseChart
          title="Facility appointment treemap"
          subtitle="Appointment intensity by facility"
          loading={loading}
          noData={!loading && !hasData}
          error={error}
          option={facilityTreemapOption({ data: facilityTreemapData })}
        />
      </div>

      <div className="col-12 col-xl-6">
        <BaseChart
          title="Facility vs age heatmap"
          subtitle="Appointment age mix by facility"
          loading={loading}
          noData={!loading && !hasData}
          error={error}
          option={ageHeatmapOption({ facilityNames: heatmapData.facilityNames, ageKeys: heatmapData.ageKeys, rows: heatmapData.rows })}
        />
      </div>

      <div className="col-12 col-xl-6">
        <BaseChart
          title="Provider relationship flow"
          subtitle="Organization to facility to provider"
          loading={loading}
          noData={!loading && !hasData}
          error={error}
          option={sankeyOption({ nodes: sankeyData.nodes, links: sankeyData.links })}
        />
      </div>
    </div>
  );
}
